<script setup>
import gsap from "gsap";

const { locale } = useI18n();

const width = ref(0);
const eventsLimit = ref(6);
const eventPageLimit = ref(6);
const endCursor = ref(null);
const filtering = ref(false);
const xTo = ref(null);
const yTo = ref(null);

const cursor = ref("");
const cursorStyle = ref({});
const cursorOnClick = ref(() => {});

const updateWidth = () => {
  width.value =
    document.body.clientWidth ||
    window.innerWidth ||
    document.documentElement.clientWidth;
  if (width.value > 1024 || matchMedia("(pointer:fine)").matches) {
    gsap.set("#cursor", { xPercent: -50, yPercent: -50 });

    xTo.value = gsap.quickTo("#cursor", "x", {
      duration: 0.1,
      ease: "power3",
    });
    yTo.value = gsap.quickTo("#cursor", "y", {
      duration: 0.1,
      ease: "power3",
    });
  }
};

const getHeight = computed(() => {
  if (width.value > 1150) return 320;

  return width.value - 700;
});

// events query
const eventsQuery = gql`
  query getEvents(
    $limit: Int!
    $endCursor: String
    $locale: LanguageCodeFilterEnum!
  ) {
    events(
      first: $limit
      after: $endCursor
      where: {
        language: $locale
        upcomingOnly: false
        orderby: { field: DATE, order: DESC }
      }
    ) {
      nodes {
        id
        eventId
        date
        eventTags {
          edges {
            node {
              name
            }
          }
        }
        title
        uri
        slug
        eventsConfig {
          title
          perex
          featuredImage {
            mediaItemUrl
          }
          datesettings {
            timeEnd
            timeFrom
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const queryVariables = {
  limit: eventsLimit.value,
  locale: locale.value.toString().toUpperCase(),
};
const { result, variables, loading, fetchMore, onResult } = useQuery(
  eventsQuery,
  queryVariables
);

const loadMore = () => {
  fetchMore({
    variables: {
      limit: eventPageLimit.value,
      locale: locale.value.toString().toUpperCase(),
      endCursor: endCursor.value,
    },
    updateQuery: (previousResult, { fetchMoreResult }) => {
      // No new data
      if (!fetchMoreResult) return previousResult;

      // Concat previous data with new data
      return {
        ...previousResult,
        events: {
          nodes: [
            ...previousResult.events.nodes,
            ...fetchMoreResult.events.nodes,
          ],
          pageInfo: { ...fetchMoreResult.events.pageInfo },
        },
      };
    },
  });
};

const events = computed(() => result?.value?.events?.nodes ?? []);
const hasMoreEvents = computed(
  () => result?.value?.events?.pageInfo?.hasNextPage
);

onResult((queryResult) => {
  endCursor.value = queryResult.data?.events?.pageInfo?.endCursor;
});

const setCursor = (value, onClick, style) => {
  cursor.value = value;
  if (cursorOnClick.value !== onClick) {
    window.document.removeEventListener("click", cursorOnClick.value);
    cursorOnClick.value = onClick;

    window.document.addEventListener("click", cursorOnClick.value);
  }
  if (cursorStyle.value !== style) {
    cursorStyle.value = style;
  }
};

onMounted(() => {
  loading.value = false;

  width.value =
    document.body.clientWidth ||
    window.innerWidth ||
    document.documentElement.clientWidth;
  if (width.value > 1024 || matchMedia("(pointer:fine)").matches) {
    gsap.set("#cursor", { xPercent: -50, yPercent: -50 });

    xTo.value = gsap.quickTo("#cursor", "x", {
      duration: 0.1,
      ease: "power3",
    });
    yTo.value = gsap.quickTo("#cursor", "y", {
      duration: 0.1,
      ease: "power3",
    });
  }

  window.addEventListener("resize", updateWidth);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateWidth);
  window.document.removeEventListener("click", cursorOnClick.value);
});

const mouseMove = (e) => {
  if (width.value > 1024 || matchMedia("(pointer:fine)").matches) {
    xTo.value(e.pageX);
    yTo.value(e.pageY);
  }
};
</script>

<template>
  <div @mousemove="mouseMove">
    <div class="overflow-hidden">
      <div class="navbar-wrapper">
        <Navbar
          :is-dark="true"
          @mouseover="
            setCursor(null, null, {
              'font-size': '14px',
              cursor: 'unset',
              'z-index': '1',
              width: '25px',
              height: '25px',
              opacity: 0.7,
            })
          "
        />
      </div>
      <div class="lg:relative">
        <Bubbles
          v-if="width > 1024"
          :key="width"
          class="w-full !absolute top-0"
          :height="getHeight"
          :start-width="700"
          :start-height="0"
          :set-cursor="setCursor"
          ref-prop="canvasEvents"
        />
        <div class="w-full pt-28 lg:pt-28 pointer-events-none">
          <div class="flex justify-start lg:justify-center pointer-events-none">
            <div
              class="max-w-7xl w-[1280px] pointer-events-none relative overflow-hidden"
            >
              <div
                class="lg:pb-6 px-0 sm:px-6 pointer-events-none"
              >
                <div class="pb-16">
                  <AnimateTitle
                    :title="`<div><span>${$t(
                      'events.tag.events.pastEvents'
                    )}</span></div>`"
                  />
                </div>
                <NuxtLinkLocale
                  class="pointer-events-auto mt-18 bg-transparent border rounded-full border-black px-4 py-2 text-sm whitespace-nowrap uppercase font-light"
                  to="events"
                  >&#x3c; {{ $t("upcoming") }}</NuxtLinkLocale
                >
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        @mouseenter="setCursor('', null, null)"
        class="flex justify-center relative"
      >
        <div class="max-w-7xl w-full px-6">
          <div v-if="events.length || loading">
            <CalendarEvent
              v-for="event in events"
              :key="event.id"
              :data="event"
              class="transition-opacity duration-500"
              :class="{ 'opacity-20': loading, 'opacity-20': filtering }"
            />
            <div v-if="hasMoreEvents" class="flex justify-center">
              <div class="max-w-7xl px-16 mt-10 md:mt-20">
                <button
                  class="transition-opacity pointer-events-auto whitespace-nowrap bg-transparent border rounded-full border-black px-4 py-2 text-sm"
                  :class="{ 'opacity-20': loading, 'opacity-20': filtering }"
                  :disabled="loading"
                  @click="loadMore()"
                >
                  <span>Načíst další</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <BecomeMember class="md:mt-36 mt-14" :set-cursor="setCursor" />
      <Footer :set-cursor="setCursor" />
      <div>
        <div
          id="cursor"
          class="hidden pointer-events-none cursor-auto lg:cursor-none lg:flex"
          :style="cursorStyle"
          :class="{
            smallCursor: cursor === null,
            hidden: !width || width < 1024,
          }"
          v-html="cursor"
        ></div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.featured-event-img {
  height: 670px;
}

#cursor {
  position: absolute;
  top: 0;
  left: -10px;
  width: 25px;
  height: 25px;
  display: none;
  font-weight: 400;

  justify-content: center;
  align-items: center;
  background-color: $color-primary;
  border-radius: 50%;
  z-index: 1000;
  cursor: none;
  transition:
    width 0.3s,
    height 0.3s,
    background-color 0.3s;
  transform: translate(-50%, -50%) translate(-150px, -150px);
  //pointer-events: none;
  text-align: center;
  @media screen {
    @media (pointer: fine) {
      display: flex;
    }
  }
}

.smallCursor {
  width: 20px;
  height: 20px;
  background-color: $color-primary;
}

.active-tag {
  background-color: $color-primary;
  color: black;
  border: white;
}

select {
  display: block;
  //width: 100%;
  width: 200px;
  font-size: 1em;
  //padding: 0.8rem 0.5rem;
  //padding: 0 12px;
  border: 1px solid #333;
  font-family: inherit;
  /** for the dropdown indicator */
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1em;

  font-family: Roboto, sans-serif;

  option {
    font-family: Roboto, sans-serif;
    border-radius: 23px;
  }
  .select-selected {
    font-family: Roboto, sans-serif;
  }
}
</style>
